/**
 * Tipos para DERAG12_36R2Service — Organizaciones humanitarias
 *
 * Datos: DERA G12 servicios (capa única `g12_36_OrganizacionesHumanitarias`)
 * Volumen: 107 features (verif. empírica 2026-05-21 WFS GetFeature numberMatched)
 * Cobertura: 97 sedes Cruz Roja + 10 Cáritas (verif. empírica 18-may)
 * Contexto: Atención humanitaria en emergencias (Decreto 197/2024)
 * Fuente: WFS IECA — https://www.ideandalucia.es/services/DERA_g12_servicios/wfs
 * CRS: EPSG:25830 (UTM30 ETRS89 — nativo, sin reproyección)
 *
 * Ruta R2: `dera/humanitarias.json.gz` (global, DOP-RE-2=α simetría WAVE-1)
 *   URL pública: `https://geodata.pteltool.es/v1/dera/humanitarias`
 *
 * HX-CAPTURA-MAXIMAL (rumbo 2 · canonizado WAVE-1-MAREMOTOS-CAT 18-may):
 * el schema persistido preserva la totalidad de los campos fuente +
 * provenance + semánticos PTEL. No se reduce al denominador común con
 * otras capas DERA G12; el dataset es soberano de su propio schema.
 * Campos específicos g12_36 preservados: `tipo`, `direccion`.
 *
 * @module types/deraG12_36
 * @version 1.0.0
 * @date 2026-05-21
 */

// =============================================================================
// FEATURE — Esquema persistido en R2 (1:1 con WFSOrganizacionesHumanitariasGeocoder.parseFeature)
// =============================================================================

/**
 * Feature G12_36 almacenada en R2.
 *
 * El layout combina cuatro grupos de campos (HX-CAPTURA-MAXIMAL pleno):
 *
 * 1. **Atributos fuente íntegros** (origen WFS DERA G12, schema empírico
 *    `DescribeFeatureType` 2026-05-21): `id_dera`, `nombre`, `tipo`,
 *    `direccion`, `cod_mun`, `municipio`, `provincia`.
 * 2. **Campos derivados de geometría** (preservan fidelidad al schema fuente):
 *    `geom_type`, `geom_pointCount`.
 * 3. **Campos semánticos PTEL** (taxonomía cerrada TUN1 + decreto):
 *    `tipologiaPTEL`, `tipoOriginal`, `decretoReferencia`, `contextoEmergencia`.
 * 4. **Campos provenance** (re-validación sin re-consultar metadata):
 *    `source_layer`, `source_endpoint`, `schema_version`, `fetched_at`.
 *
 * Además incluye coordenadas normalizadas `x`, `y` (EPSG:25830) y un
 * `name` canónico para el motor de fuzzy-matching (nombre → tipo+municipio).
 */
export interface DeraG12_36Feature {
  // ---- Identificación y coordenadas normalizadas ------------------------
  /** ID DERA original (numérico en fuente, string en R2 por compatibilidad). */
  id_dera: string;
  /** Nombre canónico para fuzzy-matching: `nombre` → `tipo en municipio` → `municipio` → fallback. */
  name: string;
  /** Coordenada X UTM30 EPSG:25830. */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830. */
  y: number;

  // ---- Atributos fuente íntegros (HX-CAPTURA-MAXIMAL §1) ----------------
  /** Nombre descriptivo de la sede/asamblea (NULL-permisivo en fuente). */
  nombre: string | null;
  /** Tipo de organización: `Cruz Roja`, `Cáritas`, etc. (NULL-permisivo en fuente). */
  tipo: string | null;
  /** Dirección postal completa (NULL-permisivo en fuente). */
  direccion: string | null;
  /** Código INE municipio (5 dígitos). */
  cod_mun: string;
  /** Nombre municipio. */
  municipio: string;
  /** Provincia. */
  provincia: string;

  // ---- Campos derivados de geometría (HX-CAPTURA-MAXIMAL §2) ------------
  /** Tipo geométrico GeoJSON original (sample 100 % `MultiPoint`). */
  geom_type: string;
  /** Cardinalidad MultiPoint (dataset n=107 100 % = 1). */
  geom_pointCount: number;

  // ---- Campos semánticos PTEL (HX-CAPTURA-MAXIMAL §3) --------------------
  /** Tipología macro TUN1 cerrada (siempre `'EMERGENCIAS'` para esta capa). */
  tipologiaPTEL: 'EMERGENCIAS';
  /** Tipo granular específico (no contamina la taxonomía cerrada). */
  tipoOriginal: 'ORGANIZACION_HUMANITARIA';
  /** Decreto de referencia institucional. */
  decretoReferencia: '197/2024';
  /** Contexto operativo de emergencia. */
  contextoEmergencia: 'HUMANITARIA_GENERAL';

  // ---- Campos provenance (HX-CAPTURA-MAXIMAL §4) ------------------------
  /** Identificador completo de la capa fuente. */
  source_layer: string;
  /** Endpoint WFS de origen. */
  source_endpoint: string;
  /** Versión del schema empírico — coordinado con SCHEMA_VERSION_G12_36. */
  schema_version: string;
  /** Timestamp de captura ISO-8601 UTC. */
  fetched_at: string;
}

// =============================================================================
// OPCIONES Y RESULTADOS
// =============================================================================

/** Opciones para búsqueda en G12_36 R2. */
export interface DeraG12_36SearchOptions {
  /** Código INE municipio (5 dígitos) — REQUERIDO. */
  codINE: string;
  /** Nombre de la sede/asamblea para fuzzy match (opcional). */
  name?: string;
  /** Umbral mínimo confianza (0-100, default: 60). */
  minConfidence?: number;
  /** Máximo resultados (default: 5). */
  maxResults?: number;
}

/** Resultado de búsqueda G12_36 R2. */
export interface DeraG12_36Result {
  /** Coordenada X UTM30 EPSG:25830. */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830. */
  y: number;
  /** Confianza (0-100). */
  confidence: number;
  /** Precisión estimada metros (DERA oficial). */
  precision: number;
  /** Identificador de fuente. */
  source: 'dera_g12_36_r2';
  /** Feature encontrada. */
  feature: DeraG12_36Feature;
  /** Score fuzzy (0-1) si aplica. */
  fuzzyScore?: number;
}

// =============================================================================
// CONSTANTES VALIDACIÓN
// =============================================================================

/** Rango válido X UTM30 Andalucía (skill ptel-geocoding). */
export const G12_36_X_RANGE = { min: 100_000, max: 900_000 };
/** Rango válido Y UTM30 Andalucía (skill ptel-geocoding). */
export const G12_36_Y_RANGE = { min: 3_900_000, max: 4_400_000 };
/** Precisión estimada DERA G12 (metros) — coordenadas oficiales EPSG:25830 nativas. */
export const G12_36_PRECISION_M = 10;

/**
 * Versión del schema empírico — debe mantenerse alineada con
 * `WFSOrganizacionesHumanitariasGeocoder.SCHEMA_VERSION_G12_36`. Incrementar
 * si el `DescribeFeatureType` upstream cambia (nuevos campos, nuevas
 * cardinalidades, cambio de tipo).
 */
export const R2_SCHEMA_VERSION_G12_36 = 'g12_36_v20260519' as const;

/** Identificador completo de la capa fuente WFS. */
export const G12_36_SOURCE_LAYER =
  'DERA_g12_servicios:g12_36_OrganizacionesHumanitarias' as const;

/** Endpoint WFS DERA G12. */
export const G12_36_SOURCE_ENDPOINT =
  'https://www.ideandalucia.es/services/DERA_g12_servicios/wfs' as const;
